import { useEffect, useRef, useState } from "react";
import { Bold, Italic, Link2, List, ListOrdered, Underline } from "lucide-react";
import { cn } from "@/lib/utils";

/** Estilos del HTML enriquecido (Tailwind resetea listas y enlaces). Se comparten entre editor y lectura. */
export const RICH_TEXT_CLASSES =
  "text-sm leading-relaxed break-words [overflow-wrap:anywhere] [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-0.5 [&_p]:my-1 [&_a]:text-primary [&_a]:underline [&_b]:font-semibold [&_strong]:font-semibold [&_h3]:font-semibold [&_h3]:text-base [&_blockquote]:border-l-2 [&_blockquote]:pl-3 [&_blockquote]:text-muted-foreground";

const BOTONES = [
  { cmd: "bold", icon: Bold, label: "Negrita (Ctrl+B)" },
  { cmd: "italic", icon: Italic, label: "Cursiva (Ctrl+I)" },
  { cmd: "underline", icon: Underline, label: "Subrayado (Ctrl+U)" },
  { cmd: "insertUnorderedList", icon: List, label: "Lista" },
  { cmd: "insertOrderedList", icon: ListOrdered, label: "Lista numerada" },
];

function estaVacio(html) {
  return !html || html.replace(/<br\s*\/?>|<\/?(p|div)>|&nbsp;|\s/gi, "") === "";
}

/**
 * Editor ligero de texto enriquecido (negrita, cursiva, subrayado, listas y enlaces).
 * No controlado: `value` solo se usa al montar; `onChange(html)` en cada cambio.
 * El HTML se sanea en el servidor antes de guardarse.
 */
export function RichTextEditor({ value = "", onChange, placeholder = "", className = "", minHeight = 96, autoFocus = false }) {
  const ref = useRef(null);
  const [vacio, setVacio] = useState(estaVacio(value));
  const [activos, setActivos] = useState({});

  useEffect(() => {
    if (ref.current) ref.current.innerHTML = value || "";
    if (autoFocus) ref.current?.focus();
    // Solo al montar: el contenido lo gestiona el propio contentEditable
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onSel = () => {
      if (!ref.current || !ref.current.contains(document.getSelection()?.anchorNode)) return;
      setActivos(Object.fromEntries(BOTONES.map((b) => [b.cmd, document.queryCommandState(b.cmd)])));
    };
    document.addEventListener("selectionchange", onSel);
    return () => document.removeEventListener("selectionchange", onSel);
  }, []);

  const emitir = () => {
    const html = ref.current?.innerHTML || "";
    setVacio(estaVacio(html));
    onChange?.(estaVacio(html) ? "" : html);
  };

  const ejecutar = (cmd, arg) => {
    ref.current?.focus();
    document.execCommand(cmd, false, arg);
    emitir();
  };

  const insertarEnlace = () => {
    const seleccion = document.getSelection()?.toString() || "";
    const url = window.prompt("Enlace (URL)", seleccion.startsWith("http") ? seleccion : "https://");
    if (!url || url === "https://") return;
    const href = /^(https?:\/\/|mailto:)/i.test(url) ? url : `https://${url}`;
    if (seleccion) ejecutar("createLink", href);
    else ejecutar("insertHTML", `<a href="${href.replace(/"/g, "&quot;")}">${href.replace(/</g, "&lt;")}</a>&nbsp;`);
  };

  return (
    <div className={cn("rounded-lg border bg-background focus-within:border-foreground/30 transition-colors", className)}>
      <div className="flex items-center gap-0.5 border-b px-1.5 py-1">
        {BOTONES.map(({ cmd, icon: Icon, label }) => (
          <button
            key={cmd}
            type="button"
            title={label}
            onMouseDown={(e) => {
              e.preventDefault();
              ejecutar(cmd);
            }}
            className={cn("h-8 w-8 rounded-md flex items-center justify-center text-muted-foreground hover:bg-muted hover:text-foreground", activos[cmd] && "bg-muted text-foreground")}
          >
            <Icon className="w-4 h-4" />
          </button>
        ))}
        <button
          type="button"
          title="Enlace"
          onMouseDown={(e) => {
            e.preventDefault();
            insertarEnlace();
          }}
          className="h-8 w-8 rounded-md flex items-center justify-center text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <Link2 className="w-4 h-4" />
        </button>
      </div>
      <div className="relative">
        {vacio && <span className="pointer-events-none absolute left-3 top-2.5 text-sm text-muted-foreground">{placeholder}</span>}
        <div
          ref={ref}
          contentEditable
          suppressContentEditableWarning
          role="textbox"
          aria-multiline="true"
          onInput={emitir}
          onPaste={(e) => {
            // Se pega como texto: el formato se da con la barra (evita estilos raros de Docs/Word)
            e.preventDefault();
            document.execCommand("insertText", false, e.clipboardData.getData("text/plain"));
          }}
          className={cn("px-3 py-2 outline-none max-h-[50vh] overflow-y-auto", RICH_TEXT_CLASSES)}
          style={{ minHeight }}
        />
      </div>
    </div>
  );
}

/** Muestra HTML ya saneado por el servidor. */
export function RichTextContent({ html, className = "" }) {
  if (!html) return null;
  return <div className={cn(RICH_TEXT_CLASSES, className)} dangerouslySetInnerHTML={{ __html: html }} />;
}
